import {getOneOfChoices} from 'course/editors/roadmapEditor/roadmapOneOf';
import {
  ROADMAP,
  type RoadmapAssignmentData,
  type RoadmapModule,
} from 'course/editors/roadmapEditor/roadmapSchema';

/**
 * The published assignment items of one roadmap, oldest module first.
 *
 * @param params.modules - The loaded modules, by id.
 * @param params.roadmapType - The roadmap to read (e.g. `student`).
 * @param params.releasedBy - A course clock string (`toRoadmapClockString`);
 *     when given, only modules whose `startDate` is at or before it count.
 *     Omitted → every module.
 * @param params.includeMathAssignments - Whether Math IDE assignments count
 *     too. Omitted → only Python IDE assignments, the ones whose projects
 *     the IDE, grading and care-hours views open.
 * @return One entry per item; `assnId` is the item's `urlId`.
 * @throws Never.
 */
export function selectRoadmapAssignments({
  modules,
  roadmapType,
  releasedBy,
  includeMathAssignments = false,
}: {
  modules: Record<string, RoadmapModule>;
  roadmapType: string;
  releasedBy?: string;
  includeMathAssignments?: boolean;
}): RoadmapAssignmentData[] {
  const itemTypes = new Set<string>([ROADMAP.ASSIGNMENT.key]);
  if (includeMathAssignments) {
    itemTypes.add(ROADMAP.MATH_ASSIGNMENT.key);
  }
  return Object.entries(modules)
    .filter(([, module]) => module.roadmapType === roadmapType)
    .filter(
      ([, module]) => releasedBy === undefined || module.startDate <= releasedBy,
    )
    .sort(([, a], [, b]) => (a.startDate > b.startDate ? 1 : -1))
    .flatMap(([id, module]) =>
      module.items
        .filter(item => item.published === true)
        .flatMap(item => {
          // A "One of" item stands for each of its Math IDE choices.
          if (item.itemType === ROADMAP.ONE_OF.key) {
            if (!includeMathAssignments) return [];
            return getOneOfChoices(item).map(assnId => ({
              module: {title: module.title, id},
              assnId,
              title: item.title,
            }));
          }
          if (!itemTypes.has(item.itemType)) return [];
          return [
            {
              module: {title: module.title, id},
              assnId: item.urlId,
              title: item.title,
            },
          ];
        }),
    );
}
